/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 MUI context
import { useArgonController } from "context";

// Firebase Auth
import { logout } from "authService";

// Images

function SidenavFooter() {
  const [controller] = useArgonController();
  const { miniSidenav } = controller;
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      // Usar la función logout de authService que cierra la sesión de Firebase correctamente
      await logout();
      // Redirigir al login después de cerrar sesión
      window.location.href = "/authentication/sign-in";
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      // Si hay error, limpiar localStorage y redirigir de todas formas
      localStorage.removeItem("adminJWT");
      localStorage.removeItem("empresaId");
      localStorage.removeItem("userId");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      window.location.href = "/authentication/sign-in";
    }
  };

  const handleConfirmLogout = async () => {
    setConfirmLogoutOpen(false);
    setIsLoggingOut(true);
    await handleLogout();
  };

  return (
    <ArgonBox opacity={miniSidenav ? 0 : 1} sx={{ transition: "opacity 200ms linear" }}>
  
      <ArgonBox display="flex" flexDirection="column">

      

        <ArgonBox textAlign="center" mt={2}>
          <ArgonBox mt={0} mb={2} textAlign="center">
            <ArgonBox
              component="button"
              type="button"
              onClick={() => setConfirmLogoutOpen(true)}
              sx={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "6px 8px",
                margin: 0,
                font: "inherit",
                fontWeight: 700,
                fontSize: "1rem",
                color: "#d32f2f",
                textTransform: "none",
                borderRadius: 1,
                lineHeight: 1.5,
                WebkitTapHighlightColor: "transparent",
                "&:hover": {
                  color: "#800020",
                  backgroundColor: "rgba(211, 47, 47, 0.06)",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(211, 47, 47, 0.55)",
                  outlineOffset: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.06)",
                },
              }}
            >
              Cerrar sesión
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>

      </ArgonBox>

      <Dialog
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
        TransitionComponent={Fade}
        transitionDuration={{ enter: 280, exit: 180 }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle id="logout-dialog-title" sx={{ pb: 2, fontWeight: 700, color: "#344767" }}>
          Cerrar sesión
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <DialogContentText
            id="logout-dialog-description"
            sx={{ fontSize: "1rem", lineHeight: 1.5, color: "#7b809a" }}
          >
            ¿Seguro que deseas cerrar sesión? Tendrás que volver a iniciar sesión para acceder al
            panel.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, flexWrap: "wrap" }}>
          <Button
            onClick={() => setConfirmLogoutOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              color: "#344767",
              borderColor: "#344767",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(52, 71, 103, 0.04)",
                borderColor: "#344767",
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
              "&:focus:not(:hover)": {
                boxShadow: "none",
              },
              "&.Mui-focusVisible": {
                outline: "2px solid #344767",
                outlineOffset: 2,
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="inherit"
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#d32f2f",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "none",
              },
              "&:focus": {
                backgroundColor: "#d32f2f",
              },
              "&.Mui-focusVisible": {
                backgroundColor: "#b71c1c",
                boxShadow: "0 0 0 3px rgba(211, 47, 47, 0.45)",
              },
              "&:active": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isLoggingOut}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
          flexDirection: "column",
          gap: 2,
          color: "#fff",
          backgroundColor: "rgba(20, 20, 30, 0.88)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Fade in={isLoggingOut} timeout={{ enter: 400, exit: 200 }} style={{ outline: "none" }}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{
              "@keyframes logoutPulse": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.85, transform: "scale(1.04)" },
              },
              animation: "logoutPulse 1.4s ease-in-out infinite",
            }}
          >
            <CircularProgress color="inherit" size={52} thickness={3.5} />
            <Typography variant="h6" fontWeight={600} letterSpacing={0.3}>
              Cerrando sesión…
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, maxWidth: 280, textAlign: "center" }}>
              Estamos cerrando tu sesión de forma segura.
            </Typography>
          </ArgonBox>
        </Fade>
      </Backdrop>
    </ArgonBox>
  );
}

export default SidenavFooter;
